import React, { useContext, useMemo } from "react";
import {
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  List,
  ListItem,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { useField, useFormikContext } from "formik";
import { AccountPayer, FormValues, GasOptionEnum } from "../types";
import HelperText from "./HelperText";
import FormContext from "src/context/FormContext";
import { getIsNSWBorderPostcode } from "../helpers";

export const GasOptions: React.FC<{ homeUser?: boolean }> = ({
  homeUser = false,
}) => {
  return (
    <Columns
      collapseBelow="md"
      space={{
        xs: "xsmall",
        md: "xxlarge",
      }}
      alignY="stretch"
    >
      <Column width="2/3">
        <GasOptionsCheckboxGroup homeUser={homeUser} />
      </Column>
      {/* HelperText */}
      <Column width="1/3">
        <HelperText>
          <Stack>
            <Text variant="body-small" tone="neutralLight">
              If you’re connecting a multi-premise building AND need only one
              shared gas line:
            </Text>
            <List
              type="decimal"
              variant="body-small"
              space="xsmall"
              tone="neutralLight"
            >
              <ListItem>
                Create a separate premises for the entire building and request a
                line-only connection.
              </ListItem>
              <ListItem>
                Then, request meter-only connections for each individual
                premises.
              </ListItem>
            </List>
          </Stack>
        </HelperText>
      </Column>
      {/* End HelperText */}
    </Columns>
  );
};

const GasOptionsCheckboxGroup: React.FC<{ homeUser?: boolean }> = ({
  homeUser = false,
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const {
    formState: { gasServices },
  } = useContext(FormContext);

  const submittedOptions = Object.entries(
    gasServices ?? ({} as Record<GasOptionEnum, { isReadOnly: boolean }>)
  )
    .filter(([, value]) => value.isReadOnly)
    .map(([key]) => key as GasOptionEnum);

  const state = values.premiseAddress.state;
  const postcode = values.premiseAddress.postcode;
  const isNSWBorder = getIsNSWBorderPostcode(postcode);
  const isNSW = state === "NSW";
  const [, { error }] = useField("gasOptions");

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ]
  );

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: GasOptionEnum
  ) => {
    if (event.target.checked) {
      setFieldValue("gasOptions", [...checkedOptions, value]);
    } else {
      setFieldValue(
        "gasOptions",
        checkedOptions.filter((item) => item !== value)
      );
    }
    if (homeUser && accountPayers.includes(AccountPayer.SOMEONE_ELSE)) {
      setFieldValue(`${value}.accountPayer`, AccountPayer.SOMEONE_ELSE);
    }
  };

  const isValidOptionByState = (
    option: GasOptionEnum,
    _state: string,
    _postcode: string
  ) => {
    if (option === GasOptionEnum.BOTH) {
      return (
        _state === "QLD" ||
        (_state === "NSW" &&
          ![
            "3644",
            "2640",
            "2641",
            "2642",
            "2643",
            "2647",
            "2712",
            "2713",
            "2714",
            "2731",
          ].includes(_postcode))
      );
    }
    if (option === GasOptionEnum.GAS_LINE) {
      return ["QLD", "VIC", "NSW", "SA"].includes(_state);
    }
    if (option === GasOptionEnum.GAS_METER) {
      return ["QLD", "SA", "VIC", "NSW"].includes(_state);
    }
    return false;
  };

  const options = [
    {
      value: GasOptionEnum.BOTH,
      label: isNSW ? "Full Connection" : "Gas line and gas meter",
      helperText: "Recommended - full connection in one request",
    },
    {
      value: GasOptionEnum.GAS_LINE,
      label: isNSW && !isNSWBorder ? "Path valve" : "Gas line",
      helperText:
        state === "SA"
          ? values.propertyType === "RESIDENTIAL"
            ? "You can request meter installation later"
            : "To request a service line for a commercial site in South Australia, call Australian Gas Networks on 1300 001 001."
          : "You can request meter installation later",
    },
    {
      value: GasOptionEnum.GAS_METER,
      label: isNSW && !isNSWBorder ? "Meter kit" : "Gas meter",
      helperText: `If a ${
        isNSW && !isNSWBorder ? "path valve" : "service line"
      } has already been installed`,
    },
  ].filter((o) => isValidOptionByState(o.value, state, postcode));

  const checkedOptions = values.gasOptions || [];

  const getIsOptionDisabled = (
    option: GasOptionEnum,
    _checkedOptions: GasOptionEnum[],
    _submittedOptions: GasOptionEnum[]
  ) => {
    if (option === GasOptionEnum.BOTH) {
      return (
        _checkedOptions.includes(GasOptionEnum.GAS_LINE) ||
        _checkedOptions.includes(GasOptionEnum.GAS_METER) ||
        _submittedOptions.includes(GasOptionEnum.BOTH)
      );
    }
    if (option === GasOptionEnum.GAS_LINE) {
      return (
        _checkedOptions.includes(GasOptionEnum.BOTH) ||
        (state === "SA" && values.propertyType === "COMMERCIAL") ||
        _submittedOptions.includes(GasOptionEnum.GAS_LINE) ||
        _submittedOptions.includes(GasOptionEnum.BOTH)
      );
    }
    if (option === GasOptionEnum.GAS_METER) {
      return (
        _checkedOptions.includes(GasOptionEnum.BOTH) ||
        _submittedOptions.includes(GasOptionEnum.GAS_METER) ||
        _submittedOptions.includes(GasOptionEnum.BOTH)
      );
    }
    return false;
  };

  return (
    <CheckboxGroup
      id="gasOptions"
      label="What needs to be installed?"
      error={!!error}
      helperText={error}
    >
      {options.map((option) => (
        <Stack key={option.value}>
          <Checkbox
            key={option.value}
            id={option.value}
            label={option.label}
            value={option.value}
            checked={checkedOptions.includes(option.value)}
            onChange={(e) => handleChange(e, option.value)}
            disabled={getIsOptionDisabled(
              option.value,
              checkedOptions,
              submittedOptions
            )}
            helperText={
              <Stack>
                <Text tone="neutralLight" variant="body-small">
                  {option.helperText}
                </Text>
              </Stack>
            }
          />
        </Stack>
      ))}
    </CheckboxGroup>
  );
};
